import type { ProgramConferenceOrWebinarFeedback } from 'models';
import { Modal, Skeleton, Text, handleAxiosError, message } from 'ui';
import { useInvalidateQueriesOnEventFeedbackFinished } from '../../hooks';
import { useCreateEventFeedbackMutation, useEventQuery } from '../../queries';
import { type EventFeedbackConfig, RateEventFeedbackForm } from '../rate-event-feedback-form';

type P = {
  eventId: string;
  isOpened: boolean;
  onCancel: () => void;
  onSuccess: () => void;
};

const config: EventFeedbackConfig[] = [
  {
    key: 'isHelpful',
    starLabel: 'How useful was the event?',
    textAreaLabel: 'What would improve your event experience?',
    textKey: 'helpfulText',
    title: 'Rate the Event',
  },
  {
    key: 'isAudioOk',
    starLabel: 'How would you rate the audio quality?',
    textAreaLabel: 'What would improve your audio quality?',
    textKey: 'audioText',
    title: 'Rate the Audio Quality',
  },
  {
    key: 'isVideoOk',
    starLabel: 'How would you rate the video quality?',
    textAreaLabel: 'What would improve your video quality?',
    textKey: 'videoText',
    title: 'Rate the Video Quality',
  },
];

const RateProgramEventFeedbackModal = ({ eventId, isOpened, onCancel, onSuccess }: P) => {
  const { isPending: isCreatingEventFeedback, mutate: createEventFeedback } = useCreateEventFeedbackMutation();
  const invalidateQueriesOnEventFeedbackFinished = useInvalidateQueriesOnEventFeedbackFinished();
  const { data, isLoading } = useEventQuery({ id: eventId });

  const onSubmit = (eventFeedback: ProgramConferenceOrWebinarFeedback) => {
    createEventFeedback(
      { data: eventFeedback, id: eventId },
      {
        onError(error) {
          handleAxiosError(error, 'Failed to send feedback.');
        },
        async onSuccess() {
          message.success('Thank you for your feedback.');
          onSuccess();
          await invalidateQueriesOnEventFeedbackFinished({ id: eventId });
        },
      },
    );
  };

  const renderModalContent = () => {
    if (isLoading || !data) {
      return <Skeleton active />;
    }

    if (data.event.feedbacked) {
      return <Text>Event was already rated</Text>;
    }

    return (
      <RateEventFeedbackForm<ProgramConferenceOrWebinarFeedback>
        config={config}
        loading={isCreatingEventFeedback}
        onCancel={onCancel}
        onSubmit={onSubmit}
      />
    );
  };

  return (
    <Modal isOpened={isOpened} maskClosable={false} onCancel={onCancel} title="Rate Event Experience" width="medium">
      <Modal.HugContent>{renderModalContent()}</Modal.HugContent>
    </Modal>
  );
};

export default RateProgramEventFeedbackModal;
